import { Box, Text, Heading, Skeleton, HStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import { useDeriveTransactionsFiltersFromBackend } from '~/packages/transactions/hooks/transactionsHooks';
import { TransactionsStatementsDrawer } from '~/packages/transactions/ui//TransactionsStatementsDrawer';
import { TransactionsChartSummary } from '~/packages/transactions/ui/TransactionsChartSummary';
import { TransactionsFiltersDrawer } from '~/packages/transactions/ui/TransactionsFiltersDrawer';
import { TransactionsSettingsMenu } from '~/packages/transactions/ui/TransactionsSettingsMenu';
import { TransactionsView } from '~/packages/transactions/ui/TransactionsView';

// Fragments
const RouteBase = () => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );
  const result = useDeriveTransactionsFiltersFromBackend({ creditFacilityIds });
  if (result.status === 'error') throw result.error;
  if (result.status === 'pending') return <Skeleton h="600px" />;
  if (result.uniqueTAccounts.length === 0) {
    return (
      <Box padding={dashboardRoutePadding}>
        <HStack mb="2.5rem">
          <Heading>Transactions</Heading>
        </HStack>
        <Text>No accounts are currently associated.</Text>
      </Box>
    );
  }

  return (
    <Box padding={dashboardRoutePadding}>
      <HStack justify="space-between" mb="2.5rem">
        <Typography variant="page-title" trim>
          Transactions
        </Typography>
        <HStack gap="1.5rem">
          <TransactionsStatementsDrawer {...result.currentFilters} />
          <TransactionsFiltersDrawer
            onFilterChange={result.setCurrentFilters}
            {...result.currentFilters}
          />
          <PermissionGuard
            permissions={(context) =>
              context.flags.productsTransactionsAdjustLiabilitiesPermissions
            }
          >
            <TransactionsSettingsMenu
              startDate={result.currentFilters.startDate}
              endDate={result.currentFilters.endDate}
            />
          </PermissionGuard>
        </HStack>
      </HStack>
      <Box>
        <TransactionsChartSummary {...result.currentFilters} />
        <TransactionsView
          filterDerivedState={result.currentFilters}
          showBackToTopButton
          hasActiveFilters
        />
      </Box>
    </Box>
  );
};

export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  return context.flags.productsTransactionsPermissions;
};
export const Route = () => {
  return (
    <PermissionGuard permissions={permissions}>
      <RouteErrorBoundary
        fallback={({ message, showErrorToast }) => {
          showErrorToast(message);
          return <Navigate to="/" />;
        }}
      >
        <RouteBase />
      </RouteErrorBoundary>
    </PermissionGuard>
  );
};
